import React, { useState, useEffect } from 'react';
import {
  Film, Tv, Music, Disc, Plus, Search, Filter, Check, X as CloseIcon,
  Eye, ThumbsUp, Calendar, Clock, Star, AlertCircle, RefreshCw,
  CheckCircle, XCircle, Trash2, Edit, Play, Download, ExternalLink
} from 'lucide-react';
import api from '../services/api';

const AdminMediaContentTab = ({ colors, showAlert }) => {
  const [mediaContent, setMediaContent] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaStats, setMediaStats] = useState(null);
  const [mediaFilter, setMediaFilter] = useState('all');
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
  const [mediaSearchTerm, setMediaSearchTerm] = useState('');
  const [ingestModalOpen, setIngestModalOpen] = useState(false);
  const [ingestTitle, setIngestTitle] = useState('');
  const [ingestYear, setIngestYear] = useState('');
  const [ingestType, setIngestType] = useState('movie');
  const [ingestAutoApprove, setIngestAutoApprove] = useState(true);
  const [ingesting, setIngesting] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [pickedResult, setPickedResult] = useState(null);
  const [ingestArtist, setIngestArtist] = useState('');
  const [ingestAlbum, setIngestAlbum] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);


  // Load media content
  const loadMediaContent = async () => {
    setMediaLoading(true);
    try {
      const params = new URLSearchParams();
      if (mediaFilter !== 'all') params.append('status', mediaFilter);
      if (mediaTypeFilter !== 'all') params.append('type', mediaTypeFilter);

      const response = await api.get(`/admin/media/all?${params.toString()}`);
      setMediaContent(response.data.media || []);
    } catch (error) {
      console.error('Error loading media:', error);
      showAlert?.({ type: 'error', message: 'Failed to load media content' });
    } finally {
      setMediaLoading(false);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const response = await api.get(`/admin/media/stats/overview`);
      setMediaStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadMediaContent();
    loadStats();
  }, [mediaFilter, mediaTypeFilter]);

  const resetIngestModal = () => {
    setIngestTitle(''); setIngestYear(''); setIngestArtist(''); setIngestAlbum('');
    setSearchResults([]); setPickedResult(null);
  };

  // Step 1: search and show results to pick from (non-music only)
  const handleSearch = async () => {
    if (!ingestTitle.trim()) { showAlert?.({ type: 'error', message: 'Title is required' }); return; }
    setSearching(true); setSearchResults([]); setPickedResult(null);
    try {
      const params = new URLSearchParams({ title: ingestTitle, type: ingestType });
      const response = await api.get(`/admin/media/search-preview?${params}`);
      const results = response.data.results || [];
      if (results.length === 0) { showAlert?.({ type: 'error', message: 'No results found' }); }
      setSearchResults(results);
    } catch (error) {
      showAlert?.({ type: 'error', message: error.response?.data?.error || 'Search failed' });
    } finally { setSearching(false); }
  };

  // Step 2: ingest the picked result
  const handleIngest = async () => {
    if (!ingestTitle.trim()) { showAlert?.({ type: 'error', message: 'Title is required' }); return; }
    setIngesting(true);
    try {
      let response;
      if (ingestType === 'music') {
        response = await api.post(`/admin/media/create`, {
          title: ingestTitle, type: 'music',
          artist: ingestArtist || undefined, album: ingestAlbum || undefined,
          autoApprove: ingestAutoApprove
        });
      } else if (pickedResult) {
        response = await api.post(`/admin/media/ingest-by-id`, {
          apiId: pickedResult.apiId, type: ingestType, autoApprove: ingestAutoApprove
        });
      } else {
        response = await api.post(`/admin/media/ingest`, {
          title: ingestTitle, type: ingestType,
          year: ingestYear ? parseInt(ingestYear) : undefined,
          autoApprove: ingestAutoApprove
        });
      }
      showAlert?.({ type: 'success', message: `"${response.data.data?.title || response.data.message}" ingested!` });
      setIngestModalOpen(false); resetIngestModal(); loadMediaContent(); loadStats();
    } catch (error) {
      showAlert?.({ type: 'error', message: error.response?.data?.error || 'Failed to ingest media' });
    } finally { setIngesting(false); }
  };

  // Approve media
  const handleApprove = async (id) => {
    try {
      await api.post(`/admin/media/${id}/approve`);
      showAlert?.({ type: 'success', message: 'Media approved!' });
      loadMediaContent();
      loadStats();
    } catch (error) {
      showAlert?.({ type: 'error', message: 'Failed to approve media' });
    }
  };

  // Reject media
  const handleReject = async (id) => {
    const reason = prompt('Rejection reason (optional):');
    try {
      await api.post(`/admin/media/${id}/reject`, { reason });
      showAlert?.({ type: 'success', message: 'Media rejected' });
      loadMediaContent();
      loadStats();
    } catch (error) {
      showAlert?.({ type: 'error', message: 'Failed to reject media' });
    }
  };

  // Toggle featured
  const handleToggleFeatured = async (media) => {
    try {
      await api.put(`/admin/media/${media.dbId}/featured`, {
        isFeatured: !media.isFeatured
      });
      showAlert?.({ type: 'success', message: `Featured status updated!` });
      loadMediaContent();
    } catch (error) {
      showAlert?.({ type: 'error', message: 'Failed to update featured status' });
    }
  };

  // Delete media
  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;

    try {
      await api.delete(`/admin/media/${id}`);
      showAlert?.({ type: 'success', message: 'Media deleted' });
      loadMediaContent();
      loadStats();
    } catch (error) {
      showAlert?.({ type: 'error', message: 'Failed to delete media' });
    }
  };

  // Filter media
  const filteredMedia = mediaContent.filter(media => {
    const matchesSearch = media.title.toLowerCase().includes(mediaSearchTerm.toLowerCase());
    return matchesSearch;
  });

  const getTypeIcon = (type) => {
    switch(type) {
      case 'movie': return <Film size={16} />;
      case 'series': return <Tv size={16} />;
      case 'anime': return <Play size={16} />;
      case 'music': return <Music size={16} />;
      default: return <Disc size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: colors.warning + '20', color: colors.warning, icon: <Clock size={8} /> },
      approved: { bg: colors.success + '20', color: colors.success, icon: <CheckCircle size={8} /> },
      rejected: { bg: colors.error + '20', color: colors.error, icon: <XCircle size={8} /> },
    };

    const style = styles[status] || styles.pending;

    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 6px',
        borderRadius: '6px',
        fontSize: '9px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color
      }}>
        {style.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </div>
    );
  };

  return (
    <div style={{ padding: '32px' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.text, marginBottom: '8px' }}>
          Media Content Management
        </h1>
        <p style={{ fontSize: '16px', color: colors.textSecondary }}>
          Manage movies, TV shows, anime, and music for ReactorHub
        </p>
      </div>

      {/* Stats */}
      {mediaStats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <StatCard label="Total Media" value={mediaStats.total} colors={colors} />
          <StatCard label="Approved" value={mediaStats.approved} colors={colors} color={colors.success} />
          <StatCard label="Pending" value={mediaStats.pending} colors={colors} color={colors.warning} />
          <StatCard label="Movies" value={mediaStats.byType?.movie || 0} colors={colors} />
          <StatCard label="TV Series" value={mediaStats.byType?.series || 0} colors={colors} />
          <StatCard label="API Calls" value={mediaStats.apiUsage?.totalCalls || 0} colors={colors} color={colors.info} />
        </div>
      )}

      {/* Action Bar */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <button
          onClick={() => setIngestModalOpen(true)}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: colors.accent,
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={18} />
          Ingest New Media
        </button>

        <button
          onClick={() => { loadMediaContent(); loadStats(); }}
          disabled={mediaLoading}
          style={{
            padding: '10px 20px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            fontSize: '14px',
            fontWeight: '600',
            cursor: mediaLoading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <RefreshCw size={18} className={mediaLoading ? 'spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '200px' }}>
          <input
            type="text"
            placeholder="Search media..."
            value={mediaSearchTerm}
            onChange={(e) => setMediaSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgSecondary,
              color: colors.text,
              fontSize: '14px'
            }}
          />
        </div>

        <select
          value={mediaFilter}
          onChange={(e) => setMediaFilter(e.target.value)}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            fontSize: '14px'
          }}
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={mediaTypeFilter}
          onChange={(e) => setMediaTypeFilter(e.target.value)}
          style={{
            padding: '10px 16px',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.bgSecondary,
            color: colors.text,
            fontSize: '14px'
          }}
        >
          <option value="all">All Types</option>
          <option value="movie">Movies</option>
          <option value="series">TV Series</option>
          <option value="anime">Anime</option>
          <option value="music">Music</option>
        </select>
      </div>

      {/* Media Grid */}
      {mediaLoading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>
          Loading media...
        </div>
      ) : filteredMedia.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>
          No media found
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
          gap: '8px'
        }}>
          {filteredMedia.map(media => (
            <MediaCard
              key={media.dbId}
              media={media}
              colors={colors}
              onApprove={handleApprove}
              onReject={handleReject}
              onDelete={handleDelete}
              onToggleFeatured={handleToggleFeatured}
              onViewDetails={(m) => { setSelectedMedia(m); setDetailModalOpen(true); }}
              getTypeIcon={getTypeIcon}
              getStatusBadge={getStatusBadge}
            />
          ))}
        </div>
      )}

      {/* Ingest Modal */}
      {ingestModalOpen && (
        <Modal colors={colors} onClose={() => setIngestModalOpen(false)}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text, marginBottom: '20px' }}>
            Ingest New Media
          </h2>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: colors.text, fontSize: '14px', fontWeight: '600' }}>
              Title
            </label>
            <input
              type="text"
              placeholder="e.g., Inception, Breaking Bad"
              value={ingestTitle}
              onChange={(e) => setIngestTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgSecondary,
                color: colors.text,
                fontSize: '14px'
              }}
              onKeyPress={(e) => e.key === 'Enter' && !searching && handleSearch()}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '8px', color: colors.text, fontSize: '14px', fontWeight: '600' }}>
              Type
            </label>
            <select
              value={ingestType}
              onChange={(e) => setIngestType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: `1px solid ${colors.border}`,
                backgroundColor: colors.bgSecondary,
                color: colors.text,
                fontSize: '14px'
              }}
            >
              <option value="movie">Movie</option>
              <option value="series">TV Series</option>
              <option value="anime">Anime</option>
              <option value="music">Music</option>
            </select>
          </div>

          {ingestType === 'music' && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: colors.text, fontSize: '14px', fontWeight: '600' }}>
                  Artist / Band
                </label>
                <input
                  type="text"
                  placeholder="e.g., Queen, Michael Jackson"
                  value={ingestArtist}
                  onChange={(e) => setIngestArtist(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.bgSecondary,
                    color: colors.text,
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: colors.text, fontSize: '14px', fontWeight: '600' }}>
                  Album
                </label>
                <input
                  type="text"
                  placeholder="e.g., A Night at the Opera"
                  value={ingestAlbum}
                  onChange={(e) => setIngestAlbum(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.bgSecondary,
                    color: colors.text,
                    fontSize: '14px'
                  }}
                />
              </div>
            </>
          )}

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.text, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ingestAutoApprove}
                onChange={(e) => setIngestAutoApprove(e.target.checked)}
                style={{ width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: '14px' }}>Auto-approve (make public immediately)</span>
            </label>
          </div>

          {/* Search results picker (non-music) */}
          {ingestType !== 'music' && searchResults.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', color: colors.text, fontSize: '14px', fontWeight: '600' }}>
                Pick the correct result:
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '260px', overflowY: 'auto' }}>
                {searchResults.map(r => (
                  <div
                    key={r.apiId}
                    onClick={() => setPickedResult(r)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px', padding: '10px',
                      borderRadius: '8px', cursor: 'pointer',
                      border: `2px solid ${pickedResult?.apiId === r.apiId ? colors.accent : colors.border}`,
                      backgroundColor: pickedResult?.apiId === r.apiId ? `${colors.accent}22` : colors.bgSecondary
                    }}
                  >
                    {r.poster && <img src={r.poster} alt="" style={{ width: '40px', height: '56px', objectFit: 'cover', borderRadius: '4px', flexShrink: 0 }} />}
                    <div>
                      <div style={{ fontWeight: '600', color: colors.text, fontSize: '14px' }}>{r.title} {r.releaseYear && <span style={{ color: colors.textSecondary, fontWeight: '400' }}>({r.releaseYear})</span>}</div>
                      {r.overview && <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '2px' }}>{r.overview}…</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => { setIngestModalOpen(false); resetIngestModal(); }}
              disabled={ingesting}
              style={{
                padding: '10px 20px', borderRadius: '8px',
                border: `1px solid ${colors.border}`, backgroundColor: colors.bgSecondary,
                color: colors.text, fontSize: '14px', fontWeight: '600',
                cursor: ingesting ? 'not-allowed' : 'pointer'
              }}
            >
              Cancel
            </button>
            {ingestType !== 'music' && (
              <button
                onClick={handleSearch}
                disabled={searching || !ingestTitle.trim()}
                style={{
                  padding: '10px 20px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#4B5563', color: '#FFFFFF',
                  fontSize: '14px', fontWeight: '600',
                  cursor: (searching || !ingestTitle.trim()) ? 'not-allowed' : 'pointer'
                }}
              >
                {searching ? 'Searching…' : 'Search'}
              </button>
            )}
            <button
              onClick={handleIngest}
              disabled={ingesting || !ingestTitle.trim() || (ingestType !== 'music' && searchResults.length > 0 && !pickedResult)}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                backgroundColor: ingesting ? colors.textTertiary : colors.accent,
                color: '#FFFFFF', fontSize: '14px', fontWeight: '600',
                cursor: (ingesting || !ingestTitle.trim() || (ingestType !== 'music' && searchResults.length > 0 && !pickedResult)) ? 'not-allowed' : 'pointer'
              }}
            >
              {ingesting ? (ingestType === 'music' ? 'Creating…' : 'Ingesting…') : (ingestType === 'music' ? 'Create Music Entry' : searchResults.length > 0 ? 'Ingest Selected' : 'Ingest Media')}
            </button>
          </div>
        </Modal>
      )}

      {/* Detail Modal */}
      {detailModalOpen && selectedMedia && (
        <DetailModal
          media={selectedMedia}
          colors={colors}
          onClose={() => setDetailModalOpen(false)}
        />
      )}
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, colors, color }) => (
  <div style={{
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: colors.bgSecondary,
    border: `1px solid ${colors.border}`
  }}>
    <div style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '8px' }}>
      {label}
    </div>
    <div style={{ fontSize: '28px', fontWeight: '700', color: color || colors.text }}>
      {value}
    </div>
  </div>
);

// Media Card Component
const MediaCard = ({ media, colors, onApprove, onReject, onDelete, onToggleFeatured, onViewDetails, getTypeIcon, getStatusBadge }) => (
  <div style={{
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: colors.bgSecondary,
    border: `1px solid ${colors.border}`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer'
  }}
  onClick={() => onViewDetails(media)}
  >
    {/* Poster */}
    <div style={{
      width: '100%',
      aspectRatio: '2/3',
      backgroundColor: colors.bgTertiary,
      backgroundImage: media.posterUrl ? `url(${media.posterUrl})` : 'none',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      position: 'relative'
    }}>
      {media.isFeatured && (
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          backgroundColor: colors.warning,
          borderRadius: '50%',
          padding: '6px'
        }}>
          <Star size={14} fill="white" color="white" />
        </div>
      )}
      <div style={{
        position: 'absolute',
        top: '8px',
        left: '8px',
        padding: '4px 8px',
        borderRadius: '6px',
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'white',
        fontSize: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px'
      }}>
        {getTypeIcon(media.type)}
        {media.type}
      </div>
    </div>

    {/* Info */}
    <div style={{ padding: '6px' }}>
      <h3 style={{
        fontSize: '10px',
        fontWeight: '600',
        color: colors.text,
        marginBottom: '4px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>
        {media.title}
      </h3>

      <div style={{ marginBottom: '6px' }}>
        {getStatusBadge(media.status)}
      </div>

      <div style={{ display: 'flex', gap: '4px', fontSize: '9px', color: colors.textSecondary, marginBottom: '6px' }}>
        <span>{media.releaseYear}</span>
        {media.rating && <span>⭐ {media.rating}/100</span>}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '2px', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
        {media.status === 'pending' && (
          <>
            <IconButton
              icon={<Check size={10} />}
              onClick={() => onApprove(media.dbId)}
              colors={colors}
              color={colors.success}
              title="Approve"
            />
            <IconButton
              icon={<CloseIcon size={10} />}
              onClick={() => onReject(media.dbId)}
              colors={colors}
              color={colors.error}
              title="Reject"
            />
          </>
        )}
        <IconButton
          icon={<Star size={10} fill={media.isFeatured ? colors.warning : 'none'} />}
          onClick={() => onToggleFeatured(media)}
          colors={colors}
          color={media.isFeatured ? colors.warning : colors.textSecondary}
          title="Toggle Featured"
        />
        <IconButton
          icon={<Trash2 size={10} />}
          onClick={() => onDelete(media.dbId, media.title)}
          colors={colors}
          color={colors.error}
          title="Delete"
        />
      </div>
    </div>
  </div>
);

// Icon Button Component
const IconButton = ({ icon, onClick, colors, color, title }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      padding: '6px',
      borderRadius: '6px',
      border: 'none',
      backgroundColor: colors.bgTertiary,
      color: color || colors.text,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    {icon}
  </button>
);

// Modal Component
const Modal = ({ children, colors, onClose }) => (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px'
  }} onClick={onClose}>
    <div style={{
      backgroundColor: colors.bg,
      borderRadius: '16px',
      padding: '24px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflow: 'auto',
      border: `1px solid ${colors.border}`
    }} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

// Detail Modal Component
const DetailModal = ({ media, colors, onClose }) => (
  <Modal colors={colors} onClose={onClose}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
      <h2 style={{ fontSize: '24px', fontWeight: '700', color: colors.text }}>
        {media.title}
      </h2>
      <button onClick={onClose} style={{
        padding: '8px',
        borderRadius: '8px',
        border: 'none',
        backgroundColor: colors.bgSecondary,
        color: colors.text,
        cursor: 'pointer'
      }}>
        <CloseIcon size={20} />
      </button>
    </div>

    {/* Poster */}
    {media.posterUrl && (
      <img
        src={media.posterUrl}
        alt={media.title}
        style={{
          width: '100%',
          borderRadius: '8px',
          marginBottom: '16px'
        }}
      />
    )}

    {/* Details */}
    <div style={{ fontSize: '14px', color: colors.textSecondary }}>
      <DetailRow label="Type" value={media.type} />
      <DetailRow label="Status" value={media.status} />
      <DetailRow label="Release Year" value={media.releaseYear} />
      <DetailRow label="Rating" value={media.rating ? `${media.rating}/100` : 'N/A'} />
      {media.artist && <DetailRow label="Artist" value={media.artist} />}
      {media.album && <DetailRow label="Album" value={media.album} />}
      {media.runtime && <DetailRow label="Runtime" value={`${media.runtime} min`} />}
      {media.genres && media.genres.length > 0 && (
        <DetailRow label="Genres" value={media.genres.map(g => g.name).join(', ')} />
      )}
      {media.directors && media.directors.length > 0 && (
        <DetailRow label="Directors" value={media.directors.join(', ')} />
      )}
      <DetailRow label="Views" value={media.viewCount || 0} />
      <DetailRow label="Reactions" value={media.reactionCount || 0} />
    </div>

    {media.overview && (
      <div style={{ marginTop: '16px' }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
          Overview
        </div>
        <p style={{ fontSize: '14px', color: colors.textSecondary, lineHeight: '1.6' }}>
          {media.overview}
        </p>
      </div>
    )}
  </Modal>
);

const DetailRow = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
    <span style={{ fontWeight: '600' }}>{label}:</span>
    <span>{value}</span>
  </div>
);

export default AdminMediaContentTab;
